import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { SearchResultDTO, RecentViewDTO } from '@amrita/shared';
import { TopBar } from '@/components/ui/TopBar';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Search, Bot, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { haptic } from '@/lib/tg';

const QUICK_TOPICS = ['Протеин', 'Креатин', 'BCAA', 'Возражение «дорого»', 'Жиросжигатели', 'Работа с 1С'];

export default function SearchPage() {
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const nav = useNavigate();
  const qc = useQueryClient();

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const searchKey = ['search', debouncedQ] as const;

  const { data, isFetching } = useQuery<SearchResultDTO>({
    queryKey: searchKey,
    queryFn: () => api.get<SearchResultDTO>(`/api/training/search?q=${encodeURIComponent(debouncedQ)}`),
    enabled: debouncedQ.length >= 2,
    staleTime: 10_000,
  });

  // Недавно открытые — только когда поле поиска пустое/короткое.
  const { data: recent } = useQuery<RecentViewDTO[]>({
    queryKey: ['recent-views'],
    queryFn: () => api.get<RecentViewDTO[]>('/api/training/recent-views'),
    enabled: q.length < 2,
    staleTime: 30_000,
  });

  const toggleBookmark = useMutation({
    mutationFn: async (articleId: string) => {
      const current = data?.articles.find((a) => a.id === articleId);
      if (current?.isBookmarked) {
        await api.delete<{ ok: true }>(`/api/training/bookmarks/${articleId}`);
      } else {
        await api.post<{ ok: true }>(`/api/training/bookmarks/${articleId}`, {});
      }
    },
    // Оптимистично переворачиваем иконку в кэше поиска.
    onMutate: async (articleId: string) => {
      await qc.cancelQueries({ queryKey: searchKey });
      const prev = qc.getQueryData<SearchResultDTO>(searchKey);
      if (prev) {
        qc.setQueryData<SearchResultDTO>(searchKey, {
          ...prev,
          articles: prev.articles.map((a) =>
            a.id === articleId ? { ...a, isBookmarked: !a.isBookmarked } : a,
          ),
        });
      }
      return { prev };
    },
    onError: (_err, _articleId, ctx) => {
      if (ctx?.prev) qc.setQueryData(searchKey, ctx.prev);
    },
    onSuccess: () => {
      haptic('light');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: searchKey });
    },
  });

  return (
    <div className="safe-bottom min-h-full">
      <TopBar title="Шпаргалка" />

      <div className="p-4 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-tg-hint" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
            placeholder="Найти в базе знаний…"
            className="w-full h-12 pl-10 pr-3 bg-tg-secondaryBg rounded-xl outline-none"
          />
        </div>

        {q.length < 2 && (
          <>
            {recent && recent.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 text-sm text-tg-hint">
                  <Clock className="w-4 h-4" />
                  Недавно открытые:
                </div>
                <div className="flex flex-wrap gap-2">
                  {recent.slice(0, 5).map((r) => (
                    <button
                      key={r.articleId}
                      onClick={() => nav(`/training/article/${r.articleId}`)}
                      className="flex items-center gap-1.5 px-3 py-2 bg-tg-secondaryBg rounded-full text-sm max-w-full"
                    >
                      {r.lessonDay != null && (
                        <span className="text-xs text-tg-hint shrink-0">Урок {r.lessonDay}</span>
                      )}
                      <span className="truncate">{r.title}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="text-sm text-tg-hint">Частые темы:</div>
            <div className="flex flex-wrap gap-2">
              {QUICK_TOPICS.map((t) => (
                <button
                  key={t}
                  onClick={() => setQ(t)}
                  className="px-3 py-2 bg-tg-secondaryBg rounded-full text-sm"
                >
                  {t}
                </button>
              ))}
            </div>
          </>
        )}

        {isFetching && <div className="text-tg-hint text-sm">Ищу…</div>}

        {data && data.articles.length === 0 && (
          <Card className="text-center space-y-3">
            <div className="text-tg-hint">
              Ничего не найдено. Попробуй переформулировать или спроси ИИ.
            </div>
            <Button variant="secondary" onClick={() => nav('/ai')}>
              <Bot className="w-5 h-5 mr-2" />
              Спросить ИИ
            </Button>
          </Card>
        )}

        {data?.articles.map((a) => (
          <Card
            key={a.id}
            className="cursor-pointer hover:bg-tg-secondaryBg/70"
            onClick={() => nav(`/training/article/${a.id}`)}
          >
            <div className="flex justify-between items-start gap-2">
              <div className="font-semibold flex-1">{a.title}</div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleBookmark.mutate(a.id);
                }}
                disabled={toggleBookmark.isPending}
                className="text-brand-orange shrink-0 px-2 py-1 -mt-1 -mr-1 rounded hover:bg-tg-secondaryBg"
                aria-label={a.isBookmarked ? 'Убрать из закладок' : 'Добавить в закладки'}
              >
                {a.isBookmarked ? '🔖' : '📑'}
              </button>
            </div>
            {a.summaryMd && <div className="text-sm text-tg-hint mt-1 line-clamp-3">{a.summaryMd}</div>}
          </Card>
        ))}
      </div>
    </div>
  );
}
